import type { ReactNode } from 'react';
import './KalahRulePaper.css';

const NORMAL_BEFORE=[6,6,6,6,6,6,0,6,6,6,6,6,6,0];
const NORMAL_AFTER=[6,6,0,7,7,7,1,7,7,6,6,6,6,0];
const SKIP_BEFORE=[2,3,0,1,0,8,20,3,2,4,1,2,3,23];
const EXTRA_BEFORE=[0,0,3,0,0,1,30,0,2,0,0,1,0,35];
const EXTRA_AFTER=[0,0,3,0,0,0,31,0,2,0,0,1,0,35];
const CAPTURE_BEFORE=[0,0,2,0,0,0,30,0,3,0,2,0,0,35];
const CAPTURE_AFTER=[0,0,0,1,0,0,34,0,0,0,2,0,0,35];
const END_BEFORE=[0,0,0,0,0,1,36,2,1,0,3,0,0,29];
const END_AFTER=[0,0,0,0,0,0,37,0,0,0,0,0,0,35];

function holeCenter(index:number):[number,number] {
  if(index===6)return[289,57];
  if(index===13)return[19,57];
  return index<6?[54+index*40,78]:[54+(12-index)*40,28];
}
function KalahPicture({name,board,path=[],emphasis=[],label}:{name:string;board:number[];path?:number[];emphasis?:number[];label:string}) {
  const points=path.map(holeCenter);
  return <svg viewBox="0 0 308 116" role="img" aria-label={label} data-kalah-board={name}
    data-rows="2" data-cols="6" data-cell-size="40" data-start="0,0" data-total={board.reduce((a,b)=>a+b,0)}>
    <rect x="1" y="1" width="306" height="114" rx="16" fill="#f2eedf" stroke="#c5c0ae" />
    {Boolean(points.length)&&<g data-arrow-purpose="sow">
      <polyline points={points.map(p=>p.join(',')).join(' ')} fill="none" stroke="#788665" strokeWidth="1.6" />
      {(()=>{const [x,y]=points.at(-1)!,[px,py]=points.at(-2)!,len=Math.hypot(x-px,y-py),dx=(x-px)/len,dy=(y-py)/len;return <path d={`M${x-24*dx+3*dy} ${y-24*dy-3*dx}L${x-19*dx} ${y-19*dy}L${x-24*dx-3*dy} ${y-24*dy+3*dx}`} fill="none" stroke="#788665" strokeWidth="1.6" />;})()}
    </g>}
    {board.map((count,index)=>{
      const [x,y]=holeCenter(index),store=index===6||index===13;
      return <g key={index} data-hole={index} data-count={count} data-store={store||undefined}>
        {store?<rect x={x-14} y="12" width="28" height="89" rx="14" fill="#e5ddc8" stroke={emphasis.includes(index)?'#788665':'#c5b99b'} strokeWidth={emphasis.includes(index)?2:1} />:
          <ellipse cx={x} cy={y} rx="15" ry="14" fill="#e5ddc8" stroke={emphasis.includes(index)?'#788665':'#c5b99b'} strokeWidth={emphasis.includes(index)?2:1} />}
        {store?<><circle cx={x} cy="32" r="3.5" fill="#806b43" /><text x={x} y="66" textAnchor="middle" fontSize="16">{count}</text></>:
          <>{Array.from({length:Math.min(count,9)},(_,n)=><circle key={n} cx={x+(n%3-1)*7} cy={y+(Math.floor(n/3)-(Math.ceil(Math.min(count,9)/3)-1)/2)*7} r="2.5" fill="#806b43" />)}
          <rect x={x-8} y={y+14} width="16" height="18" fill="#f2eedf" />
          <text x={x} y={y+29} textAnchor="middle" fontSize="14">{count}</text></>}
      </g>;
    })}
  </svg>;
}
function Pair({name,children}:{name:string;children:ReactNode}) {
  return <div className="kalah-pair" data-comparison={name}>{children}</div>;
}
function Panel({children,caption}:{children:ReactNode;caption:string}) {
  return <figure>{children}<figcaption>{caption}</figcaption></figure>;
}

export function KalahRulePaper() {
  return <article className="rule-paper kalah-paper">
    <div className="kalah-primary" data-kalah-primary>
      <section>
        <h2>終わりに、ストアの石が多い方が勝ちです。</h2>
        <p>手前の6つが自分の穴、右端が自分のストアです。</p>
        <p className="rule-small">初めは各穴に石が6個入っています。</p>
        <p>石のある自分の穴を1つ選び、中の石をすべて取ります。次の穴から反時計回りに、1個ずつ配ります。</p>
        <p>自分のストアにも1個入れ、相手のストアは飛ばします。</p>
      </section>
      <Pair name="normal-sow">
        <Panel caption="6個を配る前">
          <KalahPicture name="normal-before" board={NORMAL_BEFORE} path={[2,3,4,5,6,7,8]} emphasis={[2]}
            label="自分が手前の6つの穴、右が自分のストアの全盤。手前左から3番目の穴の6個を全て取り、次の穴から1個ずつ反時計回りに配る前です。" />
        </Panel>
        <Panel caption="1個ずつ配った後">
          <KalahPicture name="normal-after" board={NORMAL_AFTER} emphasis={[6]}
            label="前図と同じ全盤・尺度。選んだ穴が空になり、次の3つの穴、自分のストア、相手側の右2つの穴へ1個ずつ入りました。全体の石は72個です。" />
        </Panel>
      </Pair>
    </div>
    <div className="kalah-details">
      <section data-kalah-detail="skip">
        <h3>相手のストアは飛ばします。</h3>
        <figure><KalahPicture name="skip-before" board={SKIP_BEFORE} path={[5,6,7,8,9,10,11,12,0]} emphasis={[5]}
          label="全盤と尺度は配る前後の図と同じ。自分の右の穴から8個配る例では、自分のストア、相手の6つの穴を通り、相手ストアを飛ばして、自分の左の穴へ最後の1個を配ります。" />
          <figcaption>8個を配る前</figcaption>
        </figure>
      </section>
      <section data-kalah-detail="extra">
        <h3>最後の石がストアに入ると</h3>
        <p>最後の石が自分のストアに入ると、対局が終わる場合を除き、もう一度自分の手番です。</p>
        <Pair name="extra-turn">
          <Panel caption="最後の1個を配る前">
            <KalahPicture name="extra-before" board={EXTRA_BEFORE} path={[5,6]} emphasis={[5]}
              label="手前の右の穴に最後の1個があります。次は自分のストアです。両側の穴にほかの石が残っています。" />
          </Panel>
          <Panel caption="もう一度、自分の手番です。">
            <KalahPicture name="extra-after" board={EXTRA_AFTER} emphasis={[6]}
              label="前図と同じ全盤と尺度。最後の1個が自分のストアへ入って31個になりました。対局は終わらず、もう一度自分の手番です。" />
          </Panel>
        </Pair>
      </section>
      <section data-kalah-detail="capture">
        <h3>最後の石が自分の空の穴に入ると</h3>
        <p>最後の石を自分の穴へ入れるとき、その穴が空なら、その1個と向かいの穴の石をすべて自分のストアへ入れます。</p>
        <Pair name="opposite-capture">
          <Panel caption="配る前">
            <KalahPicture name="capture-before" board={CAPTURE_BEFORE} path={[2,3,4]} emphasis={[4,8]}
              label="自分の左から3番目の穴から2個配る前です。最後が入る自分の穴は空、向かいの穴に3個あります。" />
          </Panel>
          <Panel caption="4個をストアへ入れます。">
            <KalahPicture name="capture-after" board={CAPTURE_AFTER} emphasis={[6]}
              label="前図と同じ全盤と尺度。最後の1個と向かいの3個を自分のストアに入れて34個になり、その2つの穴は空です。獲得は前後の状態で示し、移動矢印を使いません。" />
          </Panel>
        </Pair>
        <p>向かいの穴が空でも、最後の1個をストアへ入れます。</p>
      </section>
      <section data-kalah-detail="end">
        <h3>対局の終わり</h3>
        <p>どちらかの6つの穴がすべて空になると終わります。石が残っている側はその石をすべて自分のストアへ入れます。</p>
        <Pair name="ending">
          <Panel caption="終わる前">
            <KalahPicture name="ending-before" board={END_BEFORE} path={[5,6]}
              label="自分の穴には最後の1個だけ残り、自分のストア36個、相手のストア29個。相手の穴には6個残っています。" />
          </Panel>
          <Panel caption="37対35で、自分の勝ちです。">
            <KalahPicture name="ending-after" board={END_AFTER} emphasis={[6]}
              label="前図と同じ全盤と尺度。自分の6穴が空になり、残った相手の6個は相手のストアへ集まりました。全穴が空で、自分37個、相手35個の合計72個です。" />
          </Panel>
        </Pair>
        <p>石の数が同じなら引き分けです。</p>
        <p>ストアの石は穴へ配りません。</p>
      </section>
    </div>
  </article>;
}
